import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { DeliveryMode } from '@spartacus/cart/base/root';
import { CheckoutPaymentTypeFacade } from '@spartacus/checkout/b2b/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import {
  CheckoutDeliveryAddressClearedEvent,
  CheckoutDeliveryAddressFacade,
  CheckoutDeliveryModesFacade
} from '@spartacus/checkout/base/root';
import {
  UserIdService,
  Address,
  UserAddressService,
  UserActions,
  ADDRESS_SERIALIZER,
  ConverterService,
  LogoutEvent,
  EventService
} from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap, filter } from 'rxjs/operators';

import { DpShippingAccountService } from './shipping-account.service';
import { DpUserPurchaseNumberStateService } from './user-purchase-number-state.service';
import { DpBaseSiteService } from '@app/core/services/base-site.service';
import { DpCart } from '@app/core/types/view/dp-cart.interface';
import { DpCartService } from '@app-modules/cart/shared/services/cart.service';
import { DpCheckoutApiService } from '@app-modules/checkout/services/checkout-api.service';

import { DeliveryModeOptions } from '../modules/delivery-mode/types';

@Injectable({ providedIn: 'root' })
export class DpCheckoutService {
  selectedPONumber$ = new BehaviorSubject<string | undefined>(undefined);
  selectedPaymentType$ = new BehaviorSubject('INVOICE');
  selectedShippingAddress$: BehaviorSubject<Address | null> = new BehaviorSubject<Address | null>(null);
  selectedDeliveryMode$ = new BehaviorSubject({} as DeliveryMode);
  selectedDeliveryModeShippingAccount$ = new BehaviorSubject('');
  selectedDeliveryModeOption$ = new BehaviorSubject({} as DeliveryModeOptions);
  selectedDeliveryModeOptionRequired$ = new BehaviorSubject(false);

  activatedRoute$ = new BehaviorSubject({} as ActivatedRoute);
  cartPONumber = '';
  processing$ = new BehaviorSubject(false);
  hasDeliveryStep$ = inject(DpBaseSiteService)
    .getDefault()
    .pipe(map((baseSite) => baseSite?.baseStore?.checkoutDeliveryStep));
  readonly #destroyRef = inject(DestroyRef);
  readonly #upNumberService = inject(DpUserPurchaseNumberStateService);
  readonly #inValidPONumber$ = new BehaviorSubject(false);
  readonly #invalidDelivery$ = new BehaviorSubject(false);
  #selectedPONumberRequired = false;

  readonly #checkoutApiService = inject(DpCheckoutApiService);

  constructor(
    private readonly checkoutStepService: CheckoutStepService,
    private readonly checkoutPaymentTypeFacade: CheckoutPaymentTypeFacade,
    private readonly checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
    private readonly checkoutDeliveryModesFacade: CheckoutDeliveryModesFacade,
    private readonly userIdService: UserIdService,
    private readonly activeCartService: ActiveCartService,
    private readonly userAddressService: UserAddressService,
    private readonly shippingAccountService: DpShippingAccountService,
    private readonly dpCartService: DpCartService,
    private readonly actions$: Actions,
    private readonly converter: ConverterService,
    private readonly eventService: EventService
  ) {
    this.checkoutPaymentTypeFacade
      .getPurchaseOrderNumberState()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.data) {
          this.cartPONumber = result.data;

          if (this.selectedPONumber$.value?.length === 0) {
            this.selectedPONumber$.next(result.data);
          }
        }
      });

    this.eventService
      .get(LogoutEvent)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.setSelectedShippingAddress(null);
        this.selectedDeliveryMode$.next({} as DeliveryMode);
      });

    this.eventService
      .get(CheckoutDeliveryAddressClearedEvent)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.setSelectedShippingAddress(this.selectedShippingAddress$.value);
        this.updateCustomerDeliveryAddress(this.selectedShippingAddress$.value);
      });

    this.checkoutDeliveryModesFacade
      .getSelectedDeliveryModeState()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ data }) => {
        if (data) {
          this.selectedDeliveryMode$.next(data);
        }
      });

    this.checkoutDeliveryAddressFacade
      .getDeliveryAddressState()
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((state) => !!state.data && !state.loading && !state.error),
        map((state) => state.data)
      )
      .subscribe((address) => {
        if (address) {
          this.setSelectedShippingAddress(address);
        }
      });
  }

  get invalidPONumber$(): Observable<boolean> {
    return this.#inValidPONumber$.asObservable();
  }

  get invalidDelivery$(): Observable<boolean> {
    return this.#invalidDelivery$.asObservable();
  }

  getCheckoutPaymentTypeFacade(): CheckoutPaymentTypeFacade {
    return this.checkoutPaymentTypeFacade;
  }

  setDeliveryMode(mode?: DeliveryMode | null): void {
    if (mode) {
      this.selectedDeliveryMode$.next(mode);
    }
  }

  setDeliveryModeShippingAccount(account: string): void {
    this.selectedDeliveryModeShippingAccount$.next(account);
  }

  setDeliveryModeOption(option: DeliveryModeOptions): void {
    this.selectedDeliveryModeOption$.next(option);
  }

  setDeliveryModeOptionRequired(required: boolean): void {
    this.selectedDeliveryModeOptionRequired$.next(required);
  }

  setPONumber(PONumber?: string): void {
    this.selectedPONumber$.next(PONumber);
  }

  setPaymentType(code: string): void {
    this.selectedPaymentType$.next(code);
  }

  setOrderOpenText(orderOpenText?: string): void {
    this.#upNumberService.state = orderOpenText;
  }

  updateSelectedShippingAddress(address: Address | null): void {
    if (address?.id === this.selectedShippingAddress$.getValue()?.id) {
      this.selectedShippingAddress$.next(address);
    }
  }

  setSelectedShippingAddress(address: Address | null): void {
    if (address?.id !== this.selectedShippingAddress$.getValue()?.id) {
      this.selectedShippingAddress$.next(address);
    }
  }

  getSelectedShippingAddress(): Observable<Address | null> {
    return this.selectedShippingAddress$.asObservable();
  }

  setActivatedRoute(activatedRoute: ActivatedRoute): void {
    this.activatedRoute$.next(activatedRoute);
  }

  setSelectedPONumberRequired(required = false): void {
    this.#selectedPONumberRequired = required;
  }

  isShippingValid(): Observable<boolean> {
    return combineLatest([
      this.selectedShippingAddress$,
      this.checkoutDeliveryAddressFacade.getDeliveryAddressState()
    ]).pipe(
      map(
        ([selectedAddress, deliveryAddressState]) =>
          !!selectedAddress &&
          !!(selectedAddress.id?.length && selectedAddress.id?.length > 0) &&
          !deliveryAddressState.loading &&
          !deliveryAddressState.error
      )
    );
  }

  isPaymentStepValid(): Observable<boolean> {
    return combineLatest([this.selectedPONumber$, this.#upNumberService.state$]).pipe(
      map(
        ([poNumber, orderOpenText]) =>
          // TODO check if formGroup can be used so we don't write duplicate validation rules
          !!(this.#selectedPONumberRequired ? poNumber && poNumber.length > 0 : true) &&
          (poNumber ? poNumber.length <= 20 : true) &&
          (orderOpenText ? orderOpenText.length <= 35 : true)
      ),
      tap((result) => this.#inValidPONumber$.next(!result))
    );
  }

  isDeliveryModeValid(): Observable<boolean> {
    return combineLatest([
      this.hasDeliveryStep$,
      this.selectedDeliveryMode$,
      this.selectedDeliveryModeOption$,
      this.selectedDeliveryModeShippingAccount$
    ]).pipe(
      map(([isDeliveryStep, mode, option, shippingAccount]) => {
        if (!isDeliveryStep) {
          return true;
        }
        if (this.selectedDeliveryModeOptionRequired$.getValue()) {
          const isValid = !!shippingAccount && option && !!Object.keys(option).length;
          this.#invalidDelivery$.next(!isValid);
          return isValid;
        }
        return !!mode && !!Object.keys(mode).length;
      })
    );
  }

  isValid(): Observable<boolean> {
    return this.checkoutStepService.activeStepIndex$.pipe(
      mergeMap((idx) => {
        const stepId = this.checkoutStepService.allSteps[idx]?.id;
        if (stepId === 'deliveryAddress') {
          return this.isShippingValid();
        }
        return of(true);
      })
    );
  }

  isShippingAndPaymentStepValid(): Observable<boolean> {
    const currentStepIndex = this.checkoutStepService.getCurrentStepIndex(this.activatedRoute$.getValue()) ?? 0;
    return this.checkoutStepService.allSteps[currentStepIndex]?.id === 'deliveryMode'
      ? combineLatest([this.isPaymentStepValid(), this.isDeliveryModeValid()]).pipe(
          map(([isPaymentValid, isDeliveryValid]) => isPaymentValid && isDeliveryValid)
        )
      : of(true);
  }

  next(): void {
    const currentStepIndex = this.checkoutStepService.getCurrentStepIndex(this.activatedRoute$.getValue()) ?? 0;
    const stepId = this.checkoutStepService.allSteps[currentStepIndex]?.id;

    if (stepId === 'deliveryAddress') {
      this.handleShippingAddressStep();
    } else if (stepId === 'deliveryMode') {
      this.processDeliveryThenPayment();
    }
  }

  processDeliveryThenPayment(): void {
    this.hasDeliveryStep$.pipe(take(1)).subscribe((isDeliveryStep) => {
      if (isDeliveryStep) {
        this.handleDeliveryModeStep();
      } else {
        this.setPayment();
      }
    });
  }

  setPayment(): void {
    this.processing$.next(true);
    combineLatest([
      this.selectedPONumber$,
      this.selectedPaymentType$,
      this.dpCartService.getActive(),
      this.hasDeliveryStep$
    ])
      .pipe(take(1))
      .subscribe(([poNumInput, selectedPaymentType, cart]) => {
        if (this.isPaymentDataChanged(selectedPaymentType, cart, poNumInput)) {
          this.checkoutPaymentTypeFacade
            .setPaymentType(selectedPaymentType, poNumInput)
            .pipe(
              take(1),
              catchError(() => of(undefined))
            )
            .subscribe(() => {
              this.processing$.next(false);
              this.checkoutStepService.next(this.activatedRoute$.getValue());
            });
        } else {
          this.processing$.next(false);
          this.checkoutStepService.next(this.activatedRoute$.getValue());
        }
      });
  }

  handleDeliveryModeStep(): void {
    this.processing$.next(true);
    const code = Object.keys(this.selectedDeliveryModeOption$.getValue()).length
      ? this.selectedDeliveryModeOption$.getValue().code
      : (this.selectedDeliveryMode$.getValue()?.code ?? '');

    this.shippingAccountService
      .setDeliveryMode(code ?? '', this.selectedDeliveryModeShippingAccount$.getValue() ?? '')
      .pipe(take(1))
      .subscribe({
        next: (_) => {
          this.processing$.next(false);
          this.setPayment();
        },
        error: (_) => this.processing$.next(false)
      });
  }

  handleShippingAddressStep(): void {
    this.processing$.next(true);
    const loadFailSubscription = this.actions$
      .pipe(ofType(UserActions.LOAD_USER_ADDRESSES_FAIL), take(1))
      .subscribe(() => {
        this.processing$.next(false);
        successfulSubscription?.unsubscribe();
      });

    const successfulSubscription = this.actions$
      .pipe(ofType(UserActions.LOAD_USER_ADDRESSES_SUCCESS), take(1))
      .subscribe(() => {
        this.checkoutStepService.next(this.activatedRoute$.getValue());
        loadFailSubscription.unsubscribe();
        this.processing$.next(false);
      });

    this.userAddressService.loadAddresses();
  }

  addAddress(address: Address): Observable<Address> {
    this.processing$.next(true);
    return this.userIdService.getUserId().pipe(
      switchMap((userId: string) => {
        address = this.converter.convert(address, ADDRESS_SERIALIZER);
        return this.#checkoutApiService.addAddress(userId, address).pipe(
          take(1),
          tap((createdAddress) => {
            this.setSelectedShippingAddress(createdAddress);
            this.processing$.next(false);
          })
        );
      })
    );
  }

  updateCustomerDeliveryAddress(address: Address | null): void {
    if (!address) {
      return;
    }
    this.processing$.next(true);
    combineLatest([this.userIdService.getUserId(), this.activeCartService.getActiveCartId()])
      .pipe(
        take(1),
        switchMap(([userId, cartId]) =>
          this.#checkoutApiService.updateDeliveryAddress(userId, cartId, address).pipe(take(1))
        )
      )
      .subscribe({ complete: () => this.processing$.next(false) });
  }

  back(): void {
    this.checkoutStepService.back(this.activatedRoute$.getValue());
  }

  private isPaymentDataChanged(selectedPaymentType: string, cart: DpCart, poNumInput?: string): boolean {
    const orderOpenText = this.#upNumberService.state;

    return (
      selectedPaymentType !== cart?.paymentType?.code ||
      poNumInput !== this.cartPONumber ||
      orderOpenText !== cart?.openText
    );
  }
}
